import { FC, useMemo, useState } from "react"
import classNames from "classnames"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router"
import Actions from "./Actions"
import { AddressInfo } from "./AddressInfo"
import Cell from "./Cell"
import EntraId from "./EntraId"
import FacilityId from "./FacilityId"
import TextField from "./TextField"
import useEntraActivation, { DialogMode } from "./useEntraActivation"
import { buildAddressPayload } from "./utils"
import { Customer } from "."
import Dialog from "@/components/shared/Dialog"
import { Icon, IconColor } from "@/components/shared/Icon"
import Spinner, { SpinnerSize } from "@/components/shared/Spinner"
import { PageRoutes } from "@/constants/routes"
import useFormStateHandler from "@/hooks/useFormStateHandler"
import { fieldConfig } from "@/pages/ControlPanel/config"
import { AppDispatch, RootState } from "@/store"
import {
   fetchEntraConsentUrl,
   updateFacilityByCustomer,
} from "@/store/facilitiesSlice"
import { Facility, FacilityForm } from "@/types/facility"
import { ColorVariant, Token } from "@/types/index"
import { resetAllFacilityData } from "@/utils/facilities"
import { convertCamelToKebab, getErrorMessage } from "@/utils/index"
import { isEmpty } from "@/utils/lodash"

interface SingleFacilityCustomerProps {
   id: string
   editModeActiveRow: string
   deleteActiveRow: string
   customerItem: Customer
   isDisabled: boolean
   onEditModeChange: (id: string) => void
   onAddUser(id: string): void
   onDelete: () => void
   onCreateFacility: () => void
}

const SingleFacilityCustomer: FC<SingleFacilityCustomerProps> = ({
   id,
   customerItem,
   editModeActiveRow,
   deleteActiveRow,
   isDisabled,
   onEditModeChange,
   onAddUser,
   onDelete,
   onCreateFacility,
}) => {
   const dispatch = useDispatch<AppDispatch>()
   const navigate = useNavigate()
   const { loadingCustomer, errorCustomer } = useSelector(
      ({ customersReducer }: RootState) => customersReducer
   )
   const { loadingFacility, errorFacility } = useSelector(
      ({ facilitiesReducer }: RootState) => facilitiesReducer
   )

   const { loadingCreateUser } = useSelector(
      ({ usersReducer }: RootState) => usersReducer
   )

   const { customerName, facilities } = customerItem
   const facility = facilities[0]
   const { id: facilityId, name, cells, wearables, users, alerts } = facility
   const [customerNameItemForm, setCustomerNameItemForm] =
      useState<Customer["customerName"]>(customerName)
   const [facilityFormState, setState] = useFormStateHandler<Facility>(facility)
   const isActiveRow = useMemo(() => {
      return editModeActiveRow === id || deleteActiveRow === id
   }, [editModeActiveRow, deleteActiveRow, id])

   const isLoading = useMemo(() => {
      return loadingCustomer || loadingFacility || loadingCreateUser
   }, [loadingCustomer, loadingFacility, loadingCreateUser])

   const isEditMode = useMemo(() => {
      return editModeActiveRow === id
   }, [editModeActiveRow, id])

   const isCountryValid = fieldConfig.country?.validate(
      facilityFormState.address.country || "USA"
   )
   const isEntraTidValid = fieldConfig.entraTid?.validate(
      facilityFormState.entraTid || ""
   )

   const customerNameValue = customerNameItemForm ?? customerName
   const isCustomerNameEmpty =
      !customerNameValue || customerNameValue.trim().length === 0

   const facilityNameValue = facilityFormState.name
   const isFacilityNameEmpty =
      !facilityNameValue || facilityNameValue.trim().length === 0

   const isFormValid =
      !isCustomerNameEmpty &&
      !isFacilityNameEmpty &&
      isCountryValid &&
      isEntraTidValid &&
      deleteActiveRow !== id

   const handleSave = () => {
      const customerId = id
      const customerName = customerNameItemForm
      const facilityId = facilityFormState.id

      const facilityData: FacilityForm = {
         id: facilityId,
         name: facilityFormState.name,
         address: buildAddressPayload(facilityFormState.address),
         ...(facilityFormState.entraTid && {
            entraTid: facilityFormState.entraTid,
         }),
      }

      const facilityDataToUpdate = {
         name: customerName,
         facilities: [
            {
               ...facilityData,
            } as FacilityForm,
         ],
      }

      if (customerId && !isEmpty(facilityDataToUpdate)) {
         dispatch(
            updateFacilityByCustomer({
               customerId,
               facilityId,
               customerData: facilityDataToUpdate,
            })
         ).then(() => {
            onEditModeChange("")
            if (facilityData.entraTid) {
               dispatch(fetchEntraConsentUrl(facilityId))
            }
         })
      }
   }

   const handleCancel = () => {
      setState(facility)
      onEditModeChange("")
   }

   const updateEntraFields = async (fields: Partial<FacilityForm>) => {
      const customerData = {
         name: customerNameItemForm ?? customerName,
         facilities: [
            {
               id: facilityId,
               name: facilityFormState.name,
               ...fields,
            } as FacilityForm,
         ],
      }

      await dispatch(
         updateFacilityByCustomer({ customerId: id, facilityId, customerData })
      ).unwrap()
   }

   const {
      entraDialogMode,
      entraActionLoading,
      openActivateDialog,
      openDeactivateDialog,
      closeEntraDialog,
      confirmEntraDialog,
   } = useEntraActivation({
      updateEntraFields,
      onDeactivated: () => onEditModeChange(""),
   })

   const handleFacilityClick = () => {
      resetAllFacilityData(dispatch)
      sessionStorage.setItem(Token.CUSTOMER_ID, id)
      sessionStorage.setItem(Token.FACILITY_ID, facilityId)
      navigate(PageRoutes.FLOOR_OVERVIEW)
   }

   return (
      <div
         className={classNames(
            "customers-facilities-table__group--customer",
            "customers-facilities-table__group--single-facility",
            {
               "customers-facilities-table__group--loading": isLoading,
               "customers-facilities-table__group--error":
                  (errorCustomer || errorFacility) && isActiveRow,
            }
         )}
      >
         {(isLoading && isActiveRow) || entraActionLoading ? (
            <Spinner size={SpinnerSize.SMALL} />
         ) : (errorCustomer || errorFacility) && isActiveRow ? (
            errorCustomer ? (
               getErrorMessage(errorCustomer)
            ) : (
               errorFacility && getErrorMessage(errorFacility)
            )
         ) : (
            <>
               <span className="customers-facilities-table__customer-info">
                  <span className="customers-facilities-table__customer-name">
                     <Cell
                        name="customerName"
                        value={
                           customerNameItemForm !== undefined
                              ? customerNameItemForm
                              : customerName
                        }
                        isEditMode={isEditMode}
                        handleCellChange={(e) => {
                           setCustomerNameItemForm(e.target.value)
                        }}
                        placeholder={fieldConfig.customerName?.placeholder}
                     >
                        <TextField
                           {...(!isEditMode && {
                              onClick: handleFacilityClick,
                           })}
                        >
                           {customerName || "-"}
                        </TextField>
                     </Cell>
                  </span>
                  {Object.entries({ name, cells, wearables, users }).map(
                     ([key, value], index) => {
                        return (
                           <span
                              key={`${key}-${index}`}
                              className={`customers-facilities-table__${convertCamelToKebab(key)}`}
                           >
                              <Cell
                                 name={key}
                                 value={
                                    facilityFormState[key as keyof Facility] !==
                                    undefined
                                       ? (facilityFormState[
                                            key as keyof Facility
                                         ] as string)
                                       : (value as string)
                                 }
                                 isEditMode={isEditMode}
                                 readonly={key !== "name"}
                                 handleCellChange={(e) => {
                                    setState({
                                       ...facilityFormState,
                                       [e.target.name]: e.target.value,
                                    })
                                 }}
                                 placeholder={
                                    fieldConfig[key as keyof Facility]
                                       ?.placeholder
                                 }
                              >
                                 <TextField
                                    name={key}
                                    {...(!isEditMode &&
                                       key === "name" && {
                                          onClick: handleFacilityClick,
                                       })}
                                 >
                                    {value}
                                 </TextField>
                                 {key === "name" && alerts?.stale > 0 && (
                                    <Icon
                                       name="alerts"
                                       color={IconColor.CERISE}
                                       size="20"
                                    />
                                 )}
                              </Cell>
                           </span>
                        )
                     }
                  )}
                  <AddressInfo
                     isEditMode={isEditMode}
                     id={id}
                     address={facility.address}
                     onChange={(e) => {
                        setState({
                           ...facilityFormState,
                           address: {
                              ...facilityFormState.address,
                              [e.target.name]: e.target.value,
                           },
                        })
                     }}
                     onStateChange={(selectedState) => {
                        setState({
                           ...facilityFormState,
                           address: {
                              ...facilityFormState.address,
                              state: selectedState,
                           },
                        })
                     }}
                     onTimezoneChange={(timezone) => {
                        setState({
                           ...facilityFormState,
                           address: {
                              ...facilityFormState.address,
                              timezone,
                           },
                        })
                     }}
                     facilityFormState={facilityFormState}
                  />
                  <EntraId
                     entraTid={
                        facilityFormState.entraTid !== undefined
                           ? facilityFormState.entraTid
                           : facility.entraTid
                     }
                     consentUrl={facility.consentUrl}
                     entraSsoActive={facility.entraSsoActive}
                     errorFacility={errorFacility}
                     isEditMode={isEditMode}
                     onEntraTidChange={(e) => {
                        setState({
                           ...facilityFormState,
                           entraTid: e.target.value,
                        })
                     }}
                     onActivate={openActivateDialog}
                     onDeactivate={openDeactivateDialog}
                     onHover={() => dispatch(fetchEntraConsentUrl(facilityId))}
                  />
                  <FacilityId facilityId={facilityId} />
               </span>
               <Actions
                  isDisabled={isDisabled}
                  isEditMode={isEditMode}
                  isFormValid={isFormValid}
                  onDelete={() => onDelete()}
                  onSave={handleSave}
                  addFacility={onCreateFacility}
                  addUser={() => onAddUser(facilityId)}
                  cancel={handleCancel}
                  onEditMode={() => onEditModeChange(id)}
               />
               {entraDialogMode && (
                  <Dialog
                     open
                     title="Are you sure you want to proceed?"
                     buttonTitle={
                        entraDialogMode === DialogMode.ACTIVATE
                           ? "Activate Entra ID login"
                           : "Deactivate Entra ID login"
                     }
                     buttonColor={
                        entraDialogMode === DialogMode.DEACTIVATE
                           ? ColorVariant.CERISE
                           : ColorVariant.HONOLULU_BLUE
                     }
                     handleClose={closeEntraDialog}
                     handleButtonClick={confirmEntraDialog}
                  />
               )}
            </>
         )}
      </div>
   )
}

export default SingleFacilityCustomer
